import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IcoBell, IcoClipboard, IcoCheckCircle, IcoX } from '../dashboard/DashIcons';

const getTypeIcon = (type) => {
  if (type === 'pto_submitted') return <IcoClipboard size={16} color="#6366f1"/>;
  if (type === 'pto_approved')  return <IcoCheckCircle size={16} color="#22c55e"/>;
  if (type === 'pto_denied')    return <IcoX size={16} color="#ef4444"/>;
  return <IcoBell size={16} color="#6b7280"/>;
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res  = await authFetch('/api/notifications');
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount     || 0);
    } catch {
      // non-fatal
    }
  }, [authFetch]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllRead = async () => {
    try {
      await authFetch('/api/notifications/read-all', { method: 'PATCH' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // non-fatal
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.read) {
      try {
        await authFetch(`/api/notifications/${n._id}/read`, { method: 'PATCH' });
        setNotifications(prev => prev.map(x => x._id === n._id ? { ...x, read: true } : x));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {
        // non-fatal
      }
    }
    setOpen(false);
    navigate('/pto');
  };

  function timeAgo(date) {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative', background: open ? '#f0f4ff' : 'transparent',
          border: 'none', cursor: 'pointer', padding: '0.35rem', borderRadius: '8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#374151', fontSize: '1.25rem', transition: 'background 0.15s'
        }}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <IcoBell size={20}/>
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', top: '0', right: '0',
              background: '#ef4444', color: 'white',
              fontSize: '0.6rem', fontWeight: '800',
              width: '16px', height: '16px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid white', lineHeight: 1
            }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0,
          background: 'white', borderRadius: '12px', width: '340px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)', border: '1px solid #e5e7eb',
          zIndex: 1100, overflow: 'hidden'
        }}>
          <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#111827' }}>
              Notifications {unreadCount > 0 && <span style={{ color: '#ef4444' }}>({unreadCount})</span>}
            </span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#6b7280', fontWeight: '600' }}>
                Mark all read
              </button>
            )}
          </div>

          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>
                No notifications yet.
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n._id}
                  onClick={() => handleNotificationClick(n)}
                  style={{
                    padding: '0.85rem 1rem', cursor: 'pointer',
                    borderBottom: '1px solid #f9fafb',
                    background: n.read ? 'white' : '#f0f4ff',
                    display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
                    transition: 'background 0.1s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={e => e.currentTarget.style.background = n.read ? 'white' : '#f0f4ff'}
                >
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '0.05rem', display: 'flex', alignItems: 'center' }}>
                    {getTypeIcon(n.type)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: n.read ? '500' : '700', fontSize: '0.82rem', color: '#111827', lineHeight: 1.4 }}>
                      {n.title}
                    </p>
                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.77rem', color: '#6b7280', lineHeight: 1.4 }}>
                      {n.message}
                    </p>
                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: '#9ca3af' }}>
                      {timeAgo(n.createdAt)}
                    </p>
                  </div>
                  {!n.read && (
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0, marginTop: '0.3rem' }} />
                  )}
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div style={{ padding: '0.6rem', borderTop: '1px solid #f3f4f6', textAlign: 'center' }}>
              <button
                onClick={() => { setOpen(false); navigate('/pto'); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#6366f1', fontWeight: '600' }}
              >
                View all PTO requests →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
